import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import apiClient from 'src/helpers/apiClient';
import type { CreateGuidanceSlotsBody, GuidanceSession } from 'src/openapi';
import ConfigUrl from '../../../config';

// Candidate: one read powers the whole feature — pricing, open slots, and the
// current active call (drives Join vs Schedule + the popup's scheduled state).
export const fetchGuidanceOverview = createAsyncThunk(
  'careerGuidance/overview',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetGuidanceOverview();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Candidate: book a slot — debits credits and creates the guidance call. The
// candidate's concerns are required and stored on the call for the advisor.
// TODO(update-spec): the booking endpoint now takes a JSON body ({ concerns }).
// Until `npm run update-spec` regenerates the client (post-deploy), send it via
// the shared axios bridge (same auth/withCredentials as the generated client).
export const bookGuidanceSlot = createAsyncThunk(
  'careerGuidance/book',
  async (
    { slotId, concerns }: { slotId: string; concerns: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${ConfigUrl.API_URL}/guidance/slots/${slotId}/book`,
        { concerns },
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Candidate: reschedule a booked call to another open slot — no re-charge.
// TODO(update-spec): new endpoint; sent via the axios bridge until the client
// is regenerated post-deploy.
export const rescheduleGuidanceCall = createAsyncThunk(
  'careerGuidance/reschedule',
  async (
    { interviewId, slotId }: { interviewId: string; slotId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${ConfigUrl.API_URL}/guidance/sessions/${interviewId}/reschedule`,
        { slot_id: slotId },
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: attach the post-call deliverables (plan PDF and/or notes) to a session.
// Multipart; sent via the axios bridge (new endpoint, pre spec-regen).
export const uploadGuidanceReport = createAsyncThunk(
  'careerGuidance/uploadReport',
  async (
    {
      interviewId,
      file,
      notes,
    }: { interviewId: string; file?: File | null; notes?: string },
    { rejectWithValue },
  ) => {
    try {
      const form = new FormData();
      if (file) form.append('report_file', file);
      if (notes != null) form.append('notes', notes);
      const response = await axios.post(
        `${ConfigUrl.API_URL}/admin/guidance/sessions/${interviewId}/report`,
        form,
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: mark a past call as a no-show. Stops it counting as a completed call,
// so the candidate returns to the first-call offer and can rebook.
// TODO(update-spec): new endpoint; sent via the axios bridge until the client
// is regenerated post-deploy.
export const markGuidanceNoShow = createAsyncThunk(
  'careerGuidance/markNoShow',
  async ({ interviewId }: { interviewId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ConfigUrl.API_URL}/admin/guidance/sessions/${interviewId}/no-show`,
        {},
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Candidate: cancel their own booked call. Full refund only when cancelling at
// least 48h before the start — the backend enforces this and returns how much
// was actually refunded.
export const cancelMyGuidanceCall = createAsyncThunk(
  'careerGuidance/cancelMyCall',
  async ({ interviewId }: { interviewId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiCancelMyGuidanceCall({ interviewId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export type GuidanceReminderRequest = {
  interviewId: string;
  // Neither offsetMinutes nor sendAt = send now.
  offsetMinutes?: number;
  sendAt?: string;
  message?: string;
};

// Host: WhatsApp reminder to the candidate — sent now, or scheduled N minutes
// before the call or for a custom time. Returns the updated session.
// TODO(update-spec): the request body is new; sent via the axios bridge until
// the client is regenerated post-deploy.
export const remindGuidanceCandidate = createAsyncThunk(
  'careerGuidance/remindCandidate',
  async (
    { interviewId, offsetMinutes, sendAt, message }: GuidanceReminderRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${ConfigUrl.API_URL}/admin/guidance/sessions/${interviewId}/remind`,
        { offset_minutes: offsetMinutes, send_at: sendAt, message },
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data as GuidanceSession;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Host: drop the pending scheduled reminder.
// TODO(update-spec): new endpoint; axios bridge until the client is regenerated.
export const cancelGuidanceReminder = createAsyncThunk(
  'careerGuidance/cancelReminder',
  async ({ interviewId }: { interviewId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${ConfigUrl.API_URL}/admin/guidance/sessions/${interviewId}/remind`,
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data as GuidanceSession;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: every booked guidance session (shared across all admins).
export const fetchAdminGuidanceSessions = createAsyncThunk(
  'careerGuidance/adminSessions',
  async (
    {
      page = 1,
      pageSize = 20,
      upcoming,
    }: { page?: number; pageSize?: number; upcoming?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetAdminGuidanceSessions({ page, pageSize, upcoming });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: the shared availability pool (all future slots, Open + Booked).
export const fetchAdminGuidanceSlots = createAsyncThunk(
  'careerGuidance/adminSlots',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiListAdminGuidanceSlots();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: add this week's slots from a weekly recurrence.
export const createAdminGuidanceSlots = createAsyncThunk(
  'careerGuidance/createSlots',
  async ({ body }: { body: CreateGuidanceSlotsBody }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiCreateAdminGuidanceSlots({ createGuidanceSlotsBody: body });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: set the max sessions bookable per day (0 = unlimited) and the
// experience cap (years) for who is offered the call (0 = no cap; omit to leave
// unchanged).
// TODO(update-spec): max_experience_years is cast until the client is regenerated.
export const setGuidanceConfig = createAsyncThunk(
  'careerGuidance/setConfig',
  async (
    {
      maxPerDay,
      maxExperienceYears,
    }: { maxPerDay: number; maxExperienceYears?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiSetAdminGuidanceConfig({
        setGuidanceConfigBody: {
          max_per_day: maxPerDay,
          ...(maxExperienceYears != null
            ? { max_experience_years: maxExperienceYears }
            : {}),
        } as any,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: cancel a booked guidance call — refunds the candidate's credits, frees
// the slot, and notifies the candidate (website + email).
export const cancelGuidanceCall = createAsyncThunk(
  'careerGuidance/cancelCall',
  async (
    {
      interviewId,
      reason,
      refundCredits = true,
    }: { interviewId: string; reason?: string; refundCredits?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiCancelAdminGuidanceCall({
        interviewId,
        // refund_credits isn't in the generated body type until the client is
        // regenerated.
        cancelGuidanceCallBody: {
          reason: reason?.trim() || null,
          refund_credits: refundCredits,
        } as any,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Admin: remove an unbooked slot from the pool.
export const deleteAdminGuidanceSlot = createAsyncThunk(
  'careerGuidance/deleteSlot',
  async ({ slotId }: { slotId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiDeleteAdminGuidanceSlot({ slotId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);
